import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import type { ElementEventBasePayload } from '@atlaskit/pragmatic-drag-and-drop/dist/types/adapter/element-adapter';
import type {
  DropTargetGetFeedbackArgs,
  DropTargetRecord,
  ElementDragPayload,
  ElementDragType,
  Input,
} from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types';
import type { ElementDropTargetEventBasePayload } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import h from 'hyperscript';

import dragIndicatorIcon from '@disy/cadenza-icons/drag-indicator.svg';
import { ariaTrueFalse } from '@disy/lib/aria';
import { assertUnreachable, requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { getLogger } from '@disy/lib/logging';
import { getScrollParent } from '@disy/lib/scroll-parent';
import type { Column, ColumnKey, ResponsiveTable } from '@disy/ui.responsive-table/responsive-table';
import { asRow, getNextRow, getPreviousRow, getRowData } from '@disy/ui.responsive-table/responsive-table-utils';
import { createIconButton } from '@disy/ui/button';
import { type Content, normalizeContent } from '@disy/ui/content';
import { icon } from '@disy/ui/icon';

import './add-drag-and-drop-column-for-manual-ordering-of-rows.css';
import i18n from './add-drag-and-drop-column-for-manual-ordering-of-rows.properties';
import { toggleDarkLight } from './responsive-table-utils';

const logger = getLogger('ui/responsive-table');

const ROW_CURRENTLY_DRAGGING_CLASS = 'is-dragging';
const DRAG_HANDLE_CLASS = 'd-responsive-table--drag-handle';

export interface DragAndDropManualOrderingConfig<T> {
  /** Allows to manipulate selection (what actually is affected by drag interaction), early. */
  onDragStart?: (dragData: DragData, responsiveTable: ResponsiveTable<T>) => void;
  /**
   * Determines whether the specified item can be repositioned to immediately precede the given next item.
   * Defaults to () => true, when not defined.
   */
  isMoveValid?: (moveToData: MoveToData, responsiveTable: ResponsiveTable<T>) => boolean;
  /**
   * Pass the item to move and the item which will be the next item after moving has been done.
   * If you want to move the item to the end of the list, set nextRowData to undefined.
   */
  orderingHandler: (moveToData: MoveToData, responsiveTable: ResponsiveTable<T>) => Promise<unknown>;
}

type ClientReorderFn = (moveToData: MoveToData) => void;

/**
 * If manual ordering config is defined, add a column with a drag handle as first column. The drag handle can then be
 * used to reorder the rows via drag and drop interaction.
 * Effective position changes are indicated with a horizontal line between the rows.
 *
 * @param responsiveTable The table the columns are manipulated of
 * @param columns The columns to manipulate
 * @param clientReorderFn Function to call for optimistic visual update (Will be reverted on change)
 * @param manualOrderingConfig If present, drag and drop column will be added
 */
export function addDragAndDropColumnForManualOrdering<T>(
  responsiveTable: ResponsiveTable<T>,
  columns: Column<T>[],
  clientReorderFn: ClientReorderFn,
  manualOrderingConfig?: DragAndDropManualOrderingConfig<T>,
): Column<T>[] {
  if (!manualOrderingConfig) {
    return columns; // Nothing to do
  }

  // Scroll corresponding scroll container when dragging rows to its viewport border
  const scrollParent = getScrollParent(responsiveTable);
  if (scrollParent) {
    autoScrollForElements({ element: scrollParent });
  }

  return [createDragAndDropColumn(responsiveTable, manualOrderingConfig, clientReorderFn), ...columns];
}

function createDragAndDropColumn<T>(
  responsiveTable: ResponsiveTable<T>,
  manualOrderingConfig: DragAndDropManualOrderingConfig<T>,
  clientReorderFn: (moveToData: MoveToData) => void,
): Column<T> {
  return {
    name: '',
    key: 'reorderRow' as ColumnKey<T>,
    width: getComputedStyle(document.body).getPropertyValue('--small-component-size'),
    renderer: (_, { tr }) => renderDragAndDopCell<T>(responsiveTable, manualOrderingConfig, clientReorderFn, tr),
  };
}

function renderDragAndDopCell<T>(
  responsiveTable: ResponsiveTable<T>,
  manualOrderingConfig: DragAndDropManualOrderingConfig<T>,
  clientReorderFn: (moveToData: MoveToData) => void,
  tr: HTMLTableRowElement,
) {
  const dragHandle = createIconButton(icon(dragIndicatorIcon), i18n('dragHandle.title'), {
    variant: 'borderless',
    size: 's',
    styleClass: DRAG_HANDLE_CLASS,
    tabIndex: -1, // Make invisible for keyboard navigation (no reordering supported here, yet)
    attrs: {
      role: 'presentation', // Make invisible for screenreader
    },
  });

  draggable({
    element: dragHandle,
    onGenerateDragPreview: (payload) => {
      const dragData = extractDragData(payload);

      if (responsiveTable.selectionMode !== 'none') {
        // If we do not have a selection that includes the dragged row, select the dragged row instead.
        // We need to do this here, so for creating a preview, the row already has its 'selected' style
        const draggingRowRowData = getRowData<T>(dragData.draggingRow);
        if (!responsiveTable.selectedRowElements.includes(dragData.draggingRow)) {
          responsiveTable.selectRows((rowData) => rowData === draggingRowRowData);
        }
      }

      // Allow usage code to intercept early, e.g. to further manipulate selection
      manualOrderingConfig.onDragStart?.(dragData, responsiveTable);

      const moveData = extractMoveData(responsiveTable);
      const rowCount = moveData.rowsToMove.length;

      // Create a new element instead of using the native one to a) have a describing preview when moving multiple
      // rows (native default preview would just use the dragged row, not all selected ones) and b) to avoid a
      // wash-out effect applied to the preview on Windows.
      // We use setCustomNativeDragPreview as it handles the lifecycle management of the preview element for us,
      // including its asynchronous attaching and detaching from DOM.
      setCustomNativeDragPreview({
        nativeSetDragImage: payload.nativeSetDragImage,
        render({ container }) {
          // Append the preview element to the provided container, whose lifetime is controlled by Pragmatic D&D
          container.appendChild(
            h('.d-responsive-table--multiple-rows-drag-preview.d-inline-stack.ellipsis.dark-bg', [
              icon(dragIndicatorIcon, { styleClass: DRAG_HANDLE_CLASS }),
              ...normalizeContent(getRowInfoForPreview(rowCount, moveData)),
            ]),
          );
        },
        getOffset({ container }) {
          return getPreviewOffset(payload, container);
        },
      });
    },
  });

  const closestEdgeData = new Map<HTMLTableRowElement, Edge | null>();

  function onDragStart(payload: ElementDropTargetEventBasePayload) {
    const { draggingRow } = extractDragData(payload);

    const effectivelyDraggingRows =
      responsiveTable.selectionMode === 'none' ? [draggingRow] : responsiveTable.selectedRowElements;

    for (const row of effectivelyDraggingRows) {
      markDragging(row);
    }

    // Apparently, there is no 'dragend' callback in the pragmatic drag and drop library, so we need to register an event.
    on(
      responsiveTable,
      'dragend',
      () => {
        for (const row of effectivelyDraggingRows) {
          unmarkDragging(row);
        }
      },
      { once: true },
    );
  }

  dropTargetForElements({
    element: tr,
    onDragStart: (payload) => {
      onDragStart(payload);
    },
    onDragEnter: (payload) => {
      const dragOverData = extractDragOverData(payload, closestEdgeData);
      const moveToData = extractMoveToData(responsiveTable, dragOverData);
      updateDropPosition(responsiveTable, manualOrderingConfig, dragOverData, moveToData);
    },
    onDrag: (payload) => {
      const dragOverData = extractDragOverData(payload, closestEdgeData);

      // LIB_WORKAROUND @atlaskit/pragmatic-drag-and-drop@1.7.3 - Remove once issue https://github.com/atlassian/pragmatic-drag-and-drop/issues/216 is resolved
      // Workaround for bug https://github.com/atlassian/pragmatic-drag-and-drop/issues/216 , where onDragStart is not
      // called when on the initial drag start position canDrag returns false. Happens, when having two rows in follow
      // selected and dragging the lower one: Dragged rows are still blue (from selection) and not marked dragging
      // visually (greyed out, half-transparent).
      // The following if statement can be removed (and onDragStart() be inlined, again) when the bug is fixed.
      if (!isMarkedDragging(dragOverData.draggedOverRow)) {
        onDragStart(payload);
      }

      const moveToData = extractMoveToData(responsiveTable, dragOverData);
      updateDropPosition(responsiveTable, manualOrderingConfig, dragOverData, moveToData);
    },
    onDragLeave: (payload) => {
      const dragOverData = extractDragOverData(payload, closestEdgeData);
      closestEdgeData.delete(dragOverData.draggedOverRow);
      clearDropPosition(dragOverData);
    },
    onDrop: async (payload) => {
      const dragOverData = extractDragOverData(payload, closestEdgeData);
      closestEdgeData.clear();
      clearDropPosition(dragOverData);

      const moveToData = extractMoveToData(responsiveTable, dragOverData);

      if (!isChange(moveToData)) {
        return;
      }

      clientReorderFn(moveToData); // Optimistically update UI

      try {
        await manualOrderingConfig.orderingHandler(moveToData, responsiveTable);
      } catch (e) {
        logger.error('Reordering item failed.\n', e);
        await responsiveTable.refresh(); // Reset failed move
      }
    },
    canDrop: (feedbackArg) => {
      // On update (mousemove while dragging, etc.) determine the closest edge (assuming the row is dragged over).
      // We reuse functionality given to us by PDD library, but instead of using it to enrich its userdata we store
      // the information in our own data structure. We do so, because we need it already in canDrop callback, which
      // is called before PDD's getData callback, that would normally be used for setting PDD userdata.
      closestEdgeData.set(tr, determineClosestEdge(tr, feedbackArg.input));

      const dragOverData = extractDragOverData(feedbackArg, closestEdgeData);
      const moveToData = extractMoveToData(responsiveTable, dragOverData);

      return isValidMove(responsiveTable, manualOrderingConfig, dragOverData, moveToData);
    },
  });

  return dragHandle;
}

function markDragging(selectedRow: HTMLTableRowElement) {
  selectedRow.classList.add(ROW_CURRENTLY_DRAGGING_CLASS);
  toggleDarkLight(selectedRow, false);
}

function unmarkDragging(draggingRow: HTMLTableRowElement) {
  draggingRow.classList.remove(ROW_CURRENTLY_DRAGGING_CLASS);
  toggleDarkLight(draggingRow, ariaTrueFalse(draggingRow, 'selected'));
}

function isMarkedDragging(draggingRow: HTMLTableRowElement) {
  return draggingRow.classList.contains(ROW_CURRENTLY_DRAGGING_CLASS);
}

function updateDropPosition<T>(
  responsiveTable: ResponsiveTable<T>,
  manualOrderingConfig: DragAndDropManualOrderingConfig<T>,
  dragOverData: DragOverData,
  moveToData: MoveToData,
) {
  const dropPosition =
    isValidMove(responsiveTable, manualOrderingConfig, dragOverData, moveToData) ? dragOverData.closestEdge : null;
  setDropPosition(dragOverData.draggedOverRow, dropPosition);
}

function isValidMove<T>(
  responsiveTable: ResponsiveTable<T>,
  { isMoveValid = () => true }: DragAndDropManualOrderingConfig<T>,
  dragOverData: DragOverData,
  moveToData: MoveToData,
) {
  // Block dragging selection into itself, as it is unclear, where to place the element then. Instead, we only allow
  // dragging to positions between selected and unselected elements.
  // If selection is disabled, only one element can be dragged, so this is not an issue there.
  if (isDraggingIntoSelection(responsiveTable, dragOverData)) {
    return false;
  }

  // Ask the usage code if the operation is valid
  return isMoveValid(moveToData, responsiveTable);
}

function isDraggingIntoSelection<T>(
  responsiveTable: ResponsiveTable<T>,
  { draggedOverRow, closestEdge }: DragOverData,
) {
  const selectedRowElements = responsiveTable.selectedRowElements;

  if (!selectedRowElements.includes(draggedOverRow)) {
    return false;
  }
  const otherSideOfNewPosition = closestEdge === 'top' ? getPreviousRow(draggedOverRow) : getNextRow(draggedOverRow);
  return otherSideOfNewPosition && selectedRowElements.includes(otherSideOfNewPosition);
}

function isChange(moveToData: MoveToData) {
  const rowsToMove = moveToData.rowsToMove;
  return rowsToMove.some((row, index) => getNextRow(row) !== (rowsToMove[index + 1] ?? moveToData.nextRow));
}

function clearDropPosition({ draggedOverRow }: DragOverData) {
  setDropPosition(draggedOverRow, null);
}

function setDropPosition(draggedOverRow: HTMLTableRowElement, dropPosition: Edge | null) {
  if (dropPosition) {
    draggedOverRow.dataset.dropPosition = dropPosition;
  } else {
    delete draggedOverRow.dataset.dropPosition;
  }
}

export interface MoveData {
  rowsToMove: HTMLTableRowElement[];
}

export interface MoveToData extends MoveData {
  /** Row following the drop position. Null if last row. */
  nextRow: HTMLTableRowElement | null;
}

function extractMoveData<T>(responsiveTable: ResponsiveTable<T>): MoveData {
  return { rowsToMove: responsiveTable.selectedRowElements };
}

function extractMoveToData<T>(responsiveTable: ResponsiveTable<T>, dragOverData: DragOverData): MoveToData {
  const { rowsToMove } = extractMoveData(responsiveTable);
  const nextRow = getNewNextRow(dragOverData.draggedOverRow, dragOverData.closestEdge, rowsToMove) ?? null;
  return { rowsToMove, nextRow };
}

function getNewNextRow(
  draggedOverRow: HTMLTableRowElement,
  closestEdge: 'top' | 'right' | 'bottom' | 'left',
  selectedRowElements: HTMLTableRowElement[],
) {
  const directNextRow = closestEdge === 'top' ? draggedOverRow : getNextRow(draggedOverRow);
  if (!directNextRow) {
    return undefined;
  }
  return getClosestNextNonSelectedRow(directNextRow, selectedRowElements);
}

function getClosestNextNonSelectedRow(
  row: HTMLTableRowElement,
  selectedRows: HTMLTableRowElement[],
): HTMLTableRowElement | undefined {
  if (selectedRows.includes(row)) {
    const previousRow = getNextRow(row);
    return previousRow ? getClosestNextNonSelectedRow(previousRow, selectedRows) : undefined;
  } else {
    return row;
  }
}

function extractDragData(payload: ElementEventBasePayload): DragData {
  const draggingRow = extractRow(payload.source);
  return { draggingRow };
}

function extractDragOverData(
  payload: ElementDropTargetEventBasePayload | DropTargetGetFeedbackArgs<ElementDragType>,
  closestEdgeData: ClosestEdgeMap,
): DragOverData {
  const draggingRow = extractRow(payload.source);
  const draggedOverRow = asRow(extractDraggedOverElement(payload));
  const closestEdge = requireNonNullable(closestEdgeData!.get(draggedOverRow));
  return { draggingRow, draggedOverRow, closestEdge };
}

function extractDraggedOverElement(
  payload: ElementDropTargetEventBasePayload | DropTargetGetFeedbackArgs<ElementDragType>,
) {
  if (isElementDropTargetEventBasePayload(payload)) {
    return payload.self.element;
  }
  if (isDragElementDropTargetGetFeedbackArgs(payload)) {
    return payload.element;
  }
  assertUnreachable(payload);
}

function isElementDropTargetEventBasePayload(
  payload: ElementEventBasePayload | ElementDropTargetEventBasePayload | DropTargetGetFeedbackArgs<ElementDragType>,
): payload is ElementDropTargetEventBasePayload {
  return (payload as ElementDropTargetEventBasePayload).self !== undefined;
}

function isDragElementDropTargetGetFeedbackArgs(
  payload: ElementEventBasePayload | ElementDropTargetEventBasePayload | DropTargetGetFeedbackArgs<ElementDragType>,
): payload is DropTargetGetFeedbackArgs<ElementDragType> {
  return (payload as DropTargetGetFeedbackArgs<ElementDragType>).element !== undefined;
}

function extractRow(record: DropTargetRecord | ElementDragPayload) {
  const element = record.element;
  return asRow(requireNonNullable(element.closest('tr')));
}

export interface DragOverData extends DragData {
  draggedOverRow: HTMLTableRowElement;
  closestEdge: Edge;
}

export interface DragData {
  draggingRow: HTMLTableRowElement;
}

function getRowInfoForPreview(rowCount: number, moveData: MoveData): Content {
  if (rowCount <= 1) {
    const onlyRow = moveData.rowsToMove[0];
    const mainCell = onlyRow.querySelector('th');

    if (mainCell) {
      return [...mainCell.children].map((child) => child.cloneNode(true));
    }
  }

  // If dragging multiple rows or not having a main column, we add a label about how many items are moved
  return i18n('multipleRowsDragPreview.label', { count: rowCount });
}

function getPreviewOffset(payload: ElementEventBasePayload, previewElement: HTMLElement): Position {
  const cursorPositionRelativeToDragHandle = getRelativePositionOfCursorFromDragHandle(payload);
  const previewElementHandlePosition = getRelativeDragHandlePosition(previewElement);

  return {
    x: previewElementHandlePosition.x + cursorPositionRelativeToDragHandle.x,
    y: previewElementHandlePosition.y + cursorPositionRelativeToDragHandle.y,
  };
}

function getRelativePositionOfCursorFromDragHandle(payload: ElementEventBasePayload) {
  const { draggingRow } = extractDragData(payload);
  const { clientX, clientY } = payload.location.initial.input;
  return relativePosition({ x: clientX, y: clientY }, getDragHandle(draggingRow).getBoundingClientRect());
}

function getRelativeDragHandlePosition(container: HTMLElement) {
  return relativePosition(getDragHandle(container).getBoundingClientRect(), container.getBoundingClientRect());
}

interface Position {
  x: number;
  y: number;
}

function relativePosition(base: Position, pos: Position): Position {
  return { x: base.x - pos.x, y: base.y - pos.y };
}

function getDragHandle(container: HTMLElement) {
  return requireNonNullable(container.querySelector<HTMLElement>(`.${DRAG_HANDLE_CLASS}`));
}

type ClosestEdgeMap = Map<HTMLTableRowElement, Edge | null>;

function determineClosestEdge(tr: HTMLTableRowElement, input: Input) {
  // Use PDD's own function to determine based on its own input information for convenience
  const data = attachClosestEdge(
    {}, // We don't have any other user data to set
    {
      element: tr,
      input,
      allowedEdges: ['top', 'bottom'],
    },
  );
  return extractClosestEdge(data); // Extract it right away
}
